import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { Reveal } from "../components/Reveal";

export default function Contact() {
  const { L } = useStore();
  const [form, setForm] = useState({ name: "", email: "", category: "product", orderNumber: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(process.env.REACT_APP_BACKEND_URL + "/api/contact", {
        name: form.name,
        email: form.email,
        message: form.message,
        category: form.category,
        order_number: form.orderNumber,
      });
      toast.success(L.contact.ok);
      setForm({ name: "", email: "", category: "product", orderNumber: "", message: "" });
    } catch {
      toast.error(L.contact.err);
    }
    setBusy(false);
  };

  const inputCls =
    "w-full bg-onyx/70 border border-gold/25 rounded-xl px-5 py-3.5 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70 transition-colors";

  return (
    <div className="pt-32 pb-24 pattern-contact min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <Reveal><p className="eyebrow">{L.contact.eyebrow}</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display text-4xl sm:text-5xl text-ivory mt-4">{L.contact.title}</h1></Reveal>
          <Reveal delay={0.14}><p className="text-sand mt-4 text-sm lg:text-base leading-relaxed max-w-md">{L.contact.sub}</p></Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 space-y-5 border border-gold/20 rounded-2xl p-7 bg-onyx/50 max-w-md" data-testid="contact-info">
              <div className="flex items-center gap-4">
                <a data-testid="contact-instagram-link" href="https://www.instagram.com/arijmisk.mtl_/" target="_blank" rel="noopener noreferrer" className="p-2.5 border border-gold/25 rounded-full text-ivory/80 hover:text-gold hover:border-gold/70 transition-colors" aria-label="Instagram">
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                </a>
                <a data-testid="contact-tiktok-link" href="https://www.tiktok.com/@arijmisk.mtl" target="_blank" rel="noopener noreferrer" className="p-2.5 border border-gold/25 rounded-full text-ivory/80 hover:text-gold hover:border-gold/70 transition-colors" aria-label="TikTok">
                  <Music2 className="w-4 h-4" strokeWidth={1.5} />
                </a>
                <span className="text-[0.7rem] tracking-[0.2em] text-ivory/60">@ARIJMISK.MTL_</span>
              </div>
              <div className="flex items-center gap-3 text-ivory/70 text-sm" data-testid="contact-address">
                <MapPin className="w-4 h-4 text-gold shrink-0" strokeWidth={1.5} />
                {L.contact.address}
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-4 border border-gold/25 rounded-2xl p-7 lg:p-9 bg-onyx/60" data-testid="contact-form">
            <input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={L.contact.namePlaceholder} aria-label={L.contact.name} className={inputCls} />
            <input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={L.contact.emailPlaceholder} aria-label={L.contact.email} className={inputCls} />
            <select data-testid="contact-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} aria-label={L.contact.category} className={inputCls}>
              <option value="product">{L.contact.catProduct}</option>
              <option value="order">{L.contact.catOrder}</option>
              <option value="mission">{L.contact.catMission}</option>
              <option value="other">{L.contact.catOther}</option>
            </select>
            {form.category === "order" && (
              <input data-testid="contact-order-number-input" value={form.orderNumber} onChange={(e) => setForm({ ...form, orderNumber: e.target.value })} placeholder={L.contact.orderNumber} className={inputCls} />
            )}
            <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={L.contact.messagePlaceholder} aria-label={L.contact.message} className={inputCls + " resize-none"} />
            <p className="text-[0.65rem] text-ivory/50 leading-relaxed">
              {L.contact.privacy}{" "}
              <Link to="/policies/privacy" className="text-gold/80 underline underline-offset-2 hover:text-gold" data-testid="contact-privacy-link">{L.policies.privacy.title}</Link>
            </p>
            <button data-testid="contact-submit-button" type="submit" disabled={busy} className="btn-gold w-full rounded-full py-3.5 text-[0.7rem] tracking-[0.25em] uppercase font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
              <Send className="w-4 h-4" /> {busy ? "…" : L.contact.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
